/**
 * Atlas API client — wraps Maggie's `/atlas/*` engine endpoints, the agent
 * control plane (`/atlas/agent`), and the ARGUS SOI comparison
 * (`/argus/compareSOI`). All routes are served in-process by Maggie.
 *
 * Response shapes live in `src/schemas/atlas.schemas.ts` (yup schemas +
 * InferType-derived static types).
 */
import axios from 'axios';
import { MaggieHttp } from '../MaggieHttp';
import type {
  AgentMessageResponse,
  AgentPinsResponse,
  AgentResponse,
  AtlasStatus,
  AtlasTemplate,
  BlockingRun,
  CompareSoiRequestRow,
  CompareSoiResponse,
  FundSummaryResponse,
  FundReconciliationTarget,
  LegacyParityResponse,
  PortfolioSummary,
  SoiUploadCounts,
  StagingOverview,
  TemplateVersionsResponse,
  TransformRunResponse,
  UpsertFundTargetInput,
} from '../../schemas/atlas.schemas';
import { blockingRunSchema } from '../../schemas/atlas.schemas';

/** Four-state agent snapshot, including the async re-promote outcome
 *  (`lastPromotionRun`). See `atlas.schemas.ts` for the gating caveats. */
export async function getAtlasStatus(): Promise<AtlasStatus> {
  const { data } = await MaggieHttp.maggie.get<AtlasStatus>('/atlas/status');
  return data;
}

export async function getStagingOverview(): Promise<StagingOverview> {
  const { data } = await MaggieHttp.maggie.post<{ target: 'staging' }, StagingOverview>(
    '/atlas/query',
    { target: 'staging' },
    // Cold-cache staging scans on big tenants can outrun the instance default —
    // give this readiness poll a modest headroom above it.
    { timeout: 90_000 }
  );
  return data;
}

export async function getSoiUploadCounts(): Promise<SoiUploadCounts> {
  // hideSuperseded=true keeps the readiness widget aligned with "current
  // reconciliation state": when a failed upload is later reran successfully,
  // the original failed row should drop out of the count so CS doesn't see
  // a phantom "1 failed" warning. Backend default is the audit view; this
  // surface opts in explicitly.
  const { data } = await MaggieHttp.maggie.get<SoiUploadCounts>(
    '/soi-agent/uploads/counts?hideSuperseded=true'
  );
  return data;
}

export async function getPortfolioSummary(): Promise<PortfolioSummary> {
  const { data } = await MaggieHttp.maggie.get<PortfolioSummary>('/atlas/portfolio-summary');
  return data;
}

/** Fetch a template by version. Omit `version` to get the current latest.
 *  `?format=structured` returns the rules + sourceClassification + reclassification
 *  payload the console renders. */
export async function getLatestTemplate(version?: number): Promise<AtlasTemplate> {
  const url =
    version === undefined
      ? '/atlas/template?format=structured'
      : `/atlas/template?format=structured&version=${version}`;
  // 404 is expected on a fresh FA (no template yet) and during a rolling deploy
  // when the older backend hasn't shipped this route; either way it's not a
  // Sentry-worthy event — the caller falls back to delivery-only state.
  const { data } = await MaggieHttp.maggie.get<AtlasTemplate>(url, { skipSentryStatuses: [404] });
  return data;
}

/** All config versions for the deployment FA plus `liveVersion` (the newest
 *  approved). Drives the version-aware "Run transform" gate: a production run
 *  executes the newest approved config, so the button is only enabled when the
 *  version the operator is viewing is that one. 404 on a fresh FA is expected. */
export async function getTemplateVersions(): Promise<TemplateVersionsResponse> {
  const { data } = await MaggieHttp.maggie.get<TemplateVersionsResponse>('/atlas/template/versions', {
    skipSentryStatuses: [404],
  });
  return data;
}

/** Distinct sourceObjects known for the deployment FA — union of past delivery
 *  sources and the latest template's declared `sourceClassification.sources`.
 *  Drives the GL upload picker so historical sources stay quick-select after
 *  the template moves on. */
export async function getKnownSourceObjects(): Promise<string[]> {
  // 404 during a rolling deploy (older backend without this route) is expected
  // and the caller falls back gracefully — don't flood Sentry with the transient.
  const { data } = await MaggieHttp.maggie.get<{ sourceObjects: string[] }>('/atlas/sourceObjects', {
    skipSentryStatuses: [404],
  });
  return data.sourceObjects;
}

export async function approveTemplate(templateId: number): Promise<AtlasTemplate> {
  const { data } = await MaggieHttp.maggie.post<never, AtlasTemplate>(
    `/atlas/template/${templateId}/approve`
  );
  return data;
}

export async function getAgentState(): Promise<AgentResponse> {
  const { data } = await MaggieHttp.maggie.get<AgentResponse>('/atlas/agent');
  return data;
}

export async function runAgent(): Promise<{ status: string }> {
  const { data } = await MaggieHttp.maggie.post<never, { status: string }>('/atlas/agent/run');
  return data;
}

/** The pin roster — durable operator/agent instructions the agent re-reads
 *  every cycle. Display-only read; unpinning goes through `postAgentMessage`
 *  below, the same channel the agent reads. */
export async function getAgentPins(): Promise<AgentPinsResponse> {
  const { data } = await MaggieHttp.maggie.get<AgentPinsResponse>('/atlas/agent/pins', {
    // 404 = the route hasn't rolled out to this environment yet; the console
    // polls this steadily, so an expected miss must not page Sentry.
    skipSentryStatuses: [404],
  });
  return data;
}

/** Post a message into the agent's channel — the only interface this UI has
 *  for unpinning (`unpin: <exact pin text>`; no dedicated endpoint). Never
 *  activates the agent. A failed unpin (e.g. the pin was already removed)
 *  422s with the roster echoed in the error body; skip Sentry for it since
 *  it's an expected race, not a bug. */
export async function postAgentMessage(message: string): Promise<AgentMessageResponse> {
  const { data } = await MaggieHttp.maggie.post<{ message: string }, AgentMessageResponse>(
    '/atlas/agent/message',
    { message },
    // 422 = failed unpin (already removed — an expected race); 404 = the
    // route hasn't rolled out to this environment yet. Neither is a bug.
    { skipSentryStatuses: [404, 422] }
  );
  return data;
}

export async function compareSoi(metricsPreview: CompareSoiRequestRow[]): Promise<CompareSoiResponse> {
  const { data } = await MaggieHttp.maggie.post<
    { metricsPreview: CompareSoiRequestRow[] },
    CompareSoiResponse
    // Fuzzy matching + an LLM fallback on the first hit make this the slowest
    // console call — generous headroom above the instance default.
  >('/argus/compareSOI', { metricsPreview }, { timeout: 120_000 });
  return data;
}

/** Thrown by `runTransform` when the API returns 409 because another transform
 *  is already running for this FA. Carries the blocking run id + start time so
 *  the UI can show "Run #N has been running since X" inline instead of just a
 *  generic axios error. */
export class TransformBusyError extends Error {
  readonly blockingRun: BlockingRun;
  readonly detail: string;
  constructor(detail: string, blockingRun: BlockingRun) {
    super(detail);
    this.name = 'TransformBusyError';
    this.detail = detail;
    this.blockingRun = blockingRun;
  }
}

/** Kick off a new Atlas transform run. The API returns the initial pipeline
 *  snapshot so the UI can seed the tracker before the next portfolio poll.
 *  A 409 means another run holds the mutex — unwraps the nested
 *  `data.message.blockingRun` payload and re-throws as `TransformBusyError`. */
export async function runTransform(): Promise<TransformRunResponse> {
  try {
    const { data } = await MaggieHttp.maggie.post<never, TransformRunResponse>(
      '/atlas/transform',
      undefined,
      {
        // 409 is part of this endpoint's contract (mutex busy). The caller
        // surfaces it in the UI; don't flood Sentry with expected conflicts.
        skipSentryStatuses: [409],
      }
    );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 409) {
      // CustomLoggerExceptionFilter wraps the FDHE payload one level deeper:
      // the actual blockingRun lives at response.data.message.blockingRun.
      // Validate the inner shape at runtime — the schema is the contract.
      const payload = err.response.data as { message?: { message?: unknown; blockingRun?: unknown } };
      const inner = payload?.message;
      if (inner && blockingRunSchema.isValidSync(inner.blockingRun)) {
        const detail = typeof inner.message === 'string' ? inner.message : 'Transform already running';
        throw new TransformBusyError(detail, inner.blockingRun as BlockingRun);
      }
    }
    throw err;
  }
}

// ── Fund-level target metrics ──

/** Read the CS-curated target for (fundId, asOfDate). Returns null when none is
 *  curated yet — the API 404s in that case, which is expected, not an error. */
export async function getFundTarget(
  fundId: number,
  asOfDate: string
): Promise<FundReconciliationTarget | null> {
  try {
    const { data } = await MaggieHttp.maggie.get<FundReconciliationTarget>(
      `/atlas/fund-target?fundId=${fundId}&asOfDate=${encodeURIComponent(asOfDate)}`,
      { skipSentryStatuses: [404] }
    );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

/** Upsert the curated target. CS-gated server-side (CUSTOMER_SUCCESS_ALL). */
export async function upsertFundTarget(input: UpsertFundTargetInput): Promise<FundReconciliationTarget> {
  const { data } = await MaggieHttp.maggie.put<UpsertFundTargetInput, FundReconciliationTarget>(
    '/atlas/fund-target',
    input
  );
  return data;
}

/** Atlas-computed fund metrics for a transform run — the "computed" side of the
 *  target diff. `transformRunId` is the console's `pipeline.runId`. */
export async function getFundSummary(
  transformRunId: number,
  fundId: number,
  asOfDate: string
): Promise<FundSummaryResponse> {
  const { data } = await MaggieHttp.maggie.get<FundSummaryResponse>(
    `/atlas/batch/${transformRunId}/fund-summary?fundId=${fundId}&asOfDate=${encodeURIComponent(asOfDate)}`
  );
  return data;
}

// ── Legacy parity ──

export interface LegacyParityRange {
  from?: string;
  to?: string;
}

function legacyParityQueryString(range: LegacyParityRange, format?: 'csv'): string {
  const params = new URLSearchParams();
  if (format) {
    params.set('format', format);
  }
  if (range.from) {
    params.set('from', range.from);
  }
  if (range.to) {
    params.set('to', range.to);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Full-grain legacy-vs-Atlas comparison for the CS flip decision. The server's
 *  own statement-timeout budget is 60s (`readWithTimeoutBudget`); give the
 *  client headroom above it rather than aborting first and racing the server's
 *  own 408. 400 (from after to), 404 (no owner run — a normal empty state),
 *  and 408 (timeout, with a narrow-and-retry hint) are all part of this
 *  endpoint's contract, handled inline by the view — none should page Sentry. */
export async function getLegacyParity(range: LegacyParityRange = {}): Promise<LegacyParityResponse> {
  const { data } = await MaggieHttp.maggie.get<LegacyParityResponse>(
    `/atlas/legacy-parity${legacyParityQueryString(range)}`,
    { timeout: 90_000, skipSentryStatuses: [400, 404, 408] }
  );
  return data;
}

/** The full, untruncated CSV grain (unlike the JSON payload's 50k bucket cap) —
 *  same query, streamed, so a bit more timeout headroom than the JSON call. Same
 *  contract statuses as the JSON call — a 408 here is if anything more likely,
 *  since it streams the full untruncated grain against the same 60s budget. */
export async function getLegacyParityCsv(
  range: LegacyParityRange = {}
): Promise<{ blob: Blob; filename: string }> {
  const response = await MaggieHttp.maggie.get<Blob>(
    `/atlas/legacy-parity${legacyParityQueryString(range, 'csv')}`,
    {
      timeout: 120_000,
      responseType: 'blob',
      skipSentryStatuses: [400, 404, 408],
    }
  );
  const disposition = response.headers['content-disposition'];
  const filename = disposition?.match(/filename="(.+)"/)?.[1] || 'legacy-parity.csv';
  return { blob: response.data, filename };
}
